/**
 * Select an Entity (the user or a team they belong to).
 */
import {Alert} from '@wandb/weave/components/Alert';
import {Select, SelectSize} from '@wandb/weave/components/Form/Select';
import {IconShareWith} from '@wandb/weave/components/Icon';
import _ from 'lodash';
import React, {useContext} from 'react';

import {useAccountTeamsList} from '../../..//pages/HomePage/HomePageSidebar/useAccountTeamsList';
import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {useUserEntitiesQuery} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {useRampFlagAccountSelector} from '../../../util/rampFeatureFlags';
import {AccountType} from '../../Search/SearchNav/types';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {WANDB_ENTITY_NAME} from '../utils';
import * as S from './SelectEntity.styles';

type SelectEntityProps = {
  size?: SelectSize;

  includeAll?: boolean;
  includePersonalEntity?: boolean;

  // If true, show the role of the entity in the label.
  showRole?: boolean;

  // If true, disable entities the viewer is not an admin for.
  entityAdminOnly?: boolean;

  value: string;
  onSelectEntity: (name: string) => void;
};

type EntityOption = {
  value: string;
  label: string;
  type: 'user' | 'team' | 'all';
  image: string | null;
  isAdmin: boolean;
  isDisabled?: boolean;
};

const OPTION_ALL: EntityOption = {
  type: 'all',
  value: '',
  label: 'All teams',
  isAdmin: false,
  image: null,
};

export const SelectEntity = ({
  size = 'medium',
  includeAll,
  includePersonalEntity,
  showRole,
  entityAdminOnly,
  value,
  onSelectEntity,
}: SelectEntityProps) => {
  const viewer = useViewer();
  const queryEntities = useUserEntitiesQuery();
  const enableAccountSelector = useRampFlagAccountSelector();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const {
    accountTeams: organizationEntities,
    loading: isOrganizationEntitiesLoading,
    error: organizationEntitiesError,
  } = useAccountTeamsList();

  const loading = enableAccountSelector
    ? isOrganizationEntitiesLoading
    : queryEntities.loading;
  const error = enableAccountSelector
    ? organizationEntitiesError
    : queryEntities.error;

  const teams =
    (enableAccountSelector
      ? (organizationEntities?.edges?.map(edge => edge.node) ?? [])
      : queryEntities?.data?.viewer?.teams?.edges.map(edge => edge.node)) ?? [];

  includePersonalEntity = enableAccountSelector
    ? selectedAccount?.accountType === AccountType.Personal
    : (includePersonalEntity ?? true);

  if (loading) {
    return <Loader active inline size="tiny" name="select-entity-loader" />;
  }
  if (error) {
    return <Alert severity="error">Could not load entities</Alert>;
  }

  const username = viewer?.username ?? '';

  const options: EntityOption[] = includeAll ? [OPTION_ALL] : [];

  // wandb first, then alphabetize
  teams.sort((a, b) => {
    const aName = a?.name ?? '';
    const bName = b?.name ?? '';
    if (aName === WANDB_ENTITY_NAME) {
      return -1;
    }
    if (bName === WANDB_ENTITY_NAME) {
      return 1;
    }
    return aName.localeCompare(bName);
  });

  for (const team of teams) {
    if (!team || team.name === username) {
      continue;
    }
    const isTeamAdmin = team?.members.some(
      m => m.username === username && m.role === 'admin'
    );
    const option: EntityOption = {
      type: 'team',
      value: team.name,
      label: team.name,
      image: team.photoUrl ?? null,
      isAdmin: isTeamAdmin,
    };
    if (entityAdminOnly && !isTeamAdmin) {
      option.isDisabled = true;
    }
    options.push(option);
  }
  if (includePersonalEntity) {
    options.push({
      type: 'user',
      value: username,
      label: username,
      image: viewer?.photoUrl ?? null,
      isAdmin: true,
    });
  }

  const selected = _.find(options, o => o.value === value && !o.isDisabled);
  const onChange = (option: EntityOption | null) => {
    if (option) {
      onSelectEntity(option.value);
    }
  };

  const formatOptionLabel = ({
    type,
    label,
    image,
    isAdmin,
    isDisabled,
  }: EntityOption) => (
    <S.EntityOption>
      <span style={{opacity: isDisabled ? 0.3 : 'inherit'}}>
        {type === 'all' ? null : image ? (
          <S.AvatarImage size={size} src={image} />
        ) : (
          <S.AvatarImage size={size}>
            <IconShareWith />
          </S.AvatarImage>
        )}
      </span>
      {label}
      {showRole && type === 'team' && (isAdmin ? ' (admin)' : ' (member)')}
      {showRole && type === 'user' && ' (personal)'}
    </S.EntityOption>
  );

  return (
    <Select<EntityOption>
      id="create-queue-entity-select"
      size={size}
      options={options}
      placeholder="Select a team"
      value={selected}
      formatOptionLabel={formatOptionLabel}
      onChange={onChange}
      isOptionDisabled={option => !!option.isDisabled}
    />
  );
};
